import { CloseOutlined } from '@ergolabs/ui-kit';
import SumsubWebSdk from '@sumsub/websdk-react';
import React, { FC } from 'react';
import { BehaviorSubject } from 'rxjs';
import styled, { css } from 'styled-components';

import {
  KycStatus,
  kycStatus$,
  setKycStatus,
} from '../../api/kycStatus/kycStatus';
import { useBodyClass } from '../../common/hooks/useBodyClass';
import { useObservable } from '../../common/hooks/useObservable';
import { device } from '../../common/utils/size';
import { getAccessToken } from './common/getAccessToken';

const APPLICANT_LOADED_EVENT = 'idCheck.onApplicantLoaded';

const APPLICATION_STATUS = 'idCheck.applicantStatus';

interface KycModalProps {
  readonly changeKycStatus: (status: KycStatus) => void;
  readonly className?: string;
  readonly active?: boolean;
}

export const kycModalActive$ = new BehaviorSubject<boolean>(false);

export const openKycModal = (): void => kycModalActive$.next(true);

export const closeKycModal = (): void => kycModalActive$.next(false);

let timerId: any = undefined;

const CloseButton = styled(CloseOutlined)`
  color: var(--spectrum-secondary-text);
  font-size: 24px;
  right: 0;
  padding: 1rem;
  position: absolute;
  top: 0;
  z-index: 1000;
`;

const _KycModal: FC<KycModalProps> = ({ className, changeKycStatus }) => {
  const [accessToken] = useObservable(getAccessToken());

  const setKycEmptyStatus = () => {
    timerId = setTimeout(() => {
      changeKycStatus(KycStatus.EMPTY);
    }, 1000);
  };

  const setOtherStatus = (
    reviewStatus: 'completed' | 'pending' | 'prechecked',
    reviewAnswer?: 'GREEN' | 'RED',
    reviewRejectType?: 'FINAL' | 'RETRY',
  ) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    if (reviewStatus === 'pending' || reviewStatus === 'prechecked') {
      changeKycStatus(KycStatus.IN_PROGRESS);
    }
    if (reviewAnswer === 'GREEN' && reviewStatus === 'completed') {
      changeKycStatus(KycStatus.PASSED);
    }
    if (
      reviewAnswer === 'RED' &&
      reviewRejectType === 'FINAL' &&
      reviewStatus === 'completed'
    ) {
      changeKycStatus(KycStatus.REJECTED);
    }
    if (
      reviewAnswer === 'RED' &&
      reviewRejectType === 'RETRY' &&
      reviewStatus === 'completed'
    ) {
      changeKycStatus(KycStatus.RETRY);
    }
  };

  return (
    <div className={className} onClick={closeKycModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={closeKycModal} />
        {accessToken && (
          <SumsubWebSdk
            accessToken={accessToken}
            expirationHandler={() => getAccessToken().toPromise() as any}
            onMessage={(message: string, payload: any) => {
              // console.log(message, payload);
              if (message === APPLICANT_LOADED_EVENT) {
                setKycEmptyStatus();
              }
              if (message === APPLICATION_STATUS) {
                setOtherStatus(
                  payload.reviewStatus,
                  payload.reviewResult?.reviewAnswer,
                  payload.reviewResult?.reviewRejectType,
                );
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

const KycModal = styled(_KycModal)`
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  height: 1px;
  justify-content: center;
  left: 0;
  min-height: 100vh;
  overflow: auto;
  opacity: 0;
  position: fixed;
  top: 0;
  transition: opacity 0.3s, visibility 0.3s;
  visibility: hidden;
  width: 100%;
  z-index: 1000;

  > .modal-content {
    position: relative;
    max-width: 500px;
    width: 100%;

    > div {
      background: #fff;
      min-height: 100vh;
    }
  }

  ${device.m} {
    padding-top: 50px;

    > .modal-content > div {
      border-radius: var(--spectrum-border-radius-l);
      min-height: initial;
    }
  }

  ${(props) =>
    props.active &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`;

export const KycListener: FC = () => {
  const [kycModalActive] = useObservable(kycModalActive$);
  useBodyClass(kycModalActive ? 'kyc-modal-opened' : 'kyc-modal-closed');

  return (
    <KycModal
      active={kycModalActive}
      changeKycStatus={(status) => setKycStatus(status)}
    />
  );
};
